import React, { useState, useContext, useEffect, useRef } from "react";
import { useParams, Link, useLocation, useNavigate } from "react-router-dom";

import SuiButton from "components/SuiButton";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Backdrop from "@mui/material/Backdrop";
import CircularProgress from "@mui/material/CircularProgress";

import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";

// Soft UI Dashboard PRO React components
import SuiBox from "components/SuiBox";
import SuiTypography from "components/SuiTypography";
import SuiBadge from "components/SuiBadge";
import SuiInput from "components/SuiInput";

import Avatar from "@mui/material/Avatar";

import { StoreContext } from "context/store";
import { AccountContext } from "context/account";

import api from "services/api";

import qs from "query-string";
import toast from "react-hot-toast";

import colors from "assets/theme/base/colors";

import Tooltip from "@mui/material/Tooltip";
import IconButton from "@mui/material/IconButton";
import ImportStylePicturesModal from "pages/de/posts/components/ImportStylePicturesModal";
import ImportMoodboardPicture from "pages/de/posts/components/ImportMoodboardPicture";
import RenderGridContentList from "pages/de/posts/components/RenderGridContentList";

import ImageIcon from "@mui/icons-material/Image";
import StyleIcon from "@mui/icons-material/Style";
import PaletteIcon from "@mui/icons-material/Palette";
import { nanoid } from "nanoid";

import { getImageSize } from "react-image-size";
import { uploadFileSizeLimit } from "utils/imageHelper";

const PhotoGrid = ({
  addGridItem,
  setAddGridItem,
  setPost,
  post,
  gridContent,
  handleClickContentStyle,
  readOnly = false,
}) => {
  const [importStyleVisible, setImportStyleVisible] = useState(false);
  const [importMoodboardVisible, setImportMoodboardVisible] = useState(false);
  const [uploading, setUploading] = useState(false);

  const uploadRef = useRef(null);
  const contentActions = [
    {
      label: "Image",
      icon: <ImageIcon fontSize="medium" />,
      handleClick: () => {
        uploadRef?.current?.click();
      },
    },
    {
      label: "Style",
      icon: <StyleIcon fontSize="medium" />,
      handleClick: () => {
        setImportStyleVisible(true);
      },
    },
    {
      label: "Moodboard",
      icon: <PaletteIcon fontSize="medium" />,
      handleClick: () => {
        setImportMoodboardVisible(true);
      },
    },
  ];

  const handleAddStyle = async (selectedStyle, selectedImgs) => {
    try {
      let styleContent = [];

      for (let i = 0; i < selectedImgs.length; i++) {
        const dimensions = await getImageSize(selectedImgs[i].url);
        styleContent.push({
          id: nanoid(),
          resource_id: selectedStyle.id,
          resource_title: selectedStyle.title,
          resource_owner_id: selectedStyle?.store?.owner_id,
          type: "style",
          img: selectedImgs[i],
          dimensions: dimensions,
          caption: "",
        });
      }
      const col = Math.min(4, styleContent.length); //default display xx images per row
      const newGridItem = {
        ...gridContent,
        col: col,
        content: [...gridContent?.content, ...styleContent],
      };
      let newContent = [...post?.metadata?.content];
      const findGridIndex = newContent.findIndex((x) => x.id == newGridItem.id);
      console.log(findGridIndex, newGridItem);

      if (findGridIndex != -1) {
        newContent[findGridIndex] = newGridItem;
        const newPostData = {
          ...post,
          metadata: {
            ...post.metadata,
            content: newContent,
          },
        };

        setPost(newPostData);
      }

      setImportStyleVisible(false);
      setAddGridItem(); //reset, allow user to add a new photo grid
    } catch (err) {
      toast.error("Failed to add Style image(s)!");
      console.log(err);
    }
  };

  const handleAddMoodboard = async (selectedMoodboards) => {
    try {
      let moodboardContent = [];
      for (let i = 0; i < selectedMoodboards.length; i++) {
        console.log(selectedMoodboards[i]);
        if (selectedMoodboards[i]?.img?.url) {
          const dimensions = await getImageSize(selectedMoodboards[i].img.url);
          moodboardContent.push({
            id: nanoid(),
            resource_id: selectedMoodboards[i].id,
            resource_title: selectedMoodboards[i]?.name,
            resource_owner_id: selectedMoodboards[i]?.owner?.id,
            type: "moodboard",
            img: selectedMoodboards[i].img,
            dimensions: dimensions,

            caption: "",
          });
        }
      }
      const col = Math.min(4, moodboardContent.length); //default display xx images per row
      const newGridItem = {
        ...gridContent,
        col: col,
        content: [...gridContent?.content, ...moodboardContent],
      };
      let newContent = [...post?.metadata?.content];
      const findGridIndex = newContent.findIndex((x) => x.id == newGridItem.id);
      if (findGridIndex != -1) {
        newContent[findGridIndex] = newGridItem;
        const newPostData = {
          ...post,
          metadata: {
            ...post.metadata,
            content: newContent,
          },
        };

        setPost(newPostData);
      }
      setImportMoodboardVisible(false);
      setAddGridItem();
    } catch (err) {
      toast.error("Failed to add Moodboard image(s)!");
      console.log(err);
    }
  };

  const handleOnFileChange = async (e) => {
    const prefileList = e?.target?.files;
    const fileList = uploadFileSizeLimit(prefileList);
    if (uploadRef?.current) {
      uploadRef.current.value = "";
    }
    if (fileList.length == 0) {
      return;
    }
    setUploading(true);
    try {
      const { data } = await api.uploads.create(fileList, {}, `/post/${nanoid()}`);
      const results = data?.uploads || [];
      const addContents = [];
      for (let i = 0; i < results.length; i++) {
        let mimeType = "image/*";
        for (let j = 0; j < fileList.length; j++) {
          if (fileList[j].name == results[i].name) {
            mimeType = fileList[j].type;
          }
        }
        const dimensions = await getImageSize(results[i].url);

        let content = {
          id: nanoid(),
          type: "image",
          img: {
            url: results[i].url,
            type: mimeType,
            name: results[i].name,
          },
          dimensions: dimensions,
          caption: "",
        };
        addContents.push(content);
      }

      const col = Math.min(4, addContents.length); //default display xx images per row
      const newGridItem = {
        ...gridContent,
        col: col,
        content: [...gridContent?.content, ...addContents],
      };
      let newContent = [...post?.metadata?.content];
      const findGridIndex = newContent.findIndex((x) => x.id == newGridItem.id);

      if (findGridIndex != -1) {
        newContent[findGridIndex] = newGridItem;
        const newPostData = {
          ...post,
          metadata: {
            ...post.metadata,
            content: newContent,
          },
        };

        setPost(newPostData);
      }

      toast.success("Upload successfully!");
      setUploading(false);
      setAddGridItem();
    } catch (err) {
      console.log(err);
      toast.error("Upload failed!");

      setUploading(false);
    }
  };

  return (
    <SuiBox>
      <Backdrop sx={{ color: "#fff", zIndex: (theme) => 99999 }} open={uploading}>
        <CircularProgress color="inherit" />
      </Backdrop>

      {gridContent?.content && gridContent.content.length > 0 ? (
        <SuiBox sx={{ width: "100%" }}>
          <RenderGridContentList
            list={gridContent.content}
            col={gridContent?.col}
            gap={gridContent?.gap}
            handleClickContentStyle={handleClickContentStyle}
            post={post}
            setPost={setPost}
            readOnly={readOnly}
          />
        </SuiBox>
      ) : readOnly ? null : (
        <SuiBox sx={{ width: "100%", height: "300px" }}>
          <SuiBox
            sx={{
              height: "100%",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <SuiTypography variant="h6" color="text" sx={{ my: 1 }}>
              Add images to your grid:
            </SuiTypography>
            <SuiBox display="flex" alignItems="center" justifyContent="center" gap={3}>
              {contentActions.map((item, index) => (
                <SuiBox key={index} sx={{ textAlign: "center" }} onClick={item.handleClick}>
                  <IconButton
                    size="large"
                    color="dark"
                    sx={{
                      background: "50%",
                      backgroundColor: colors?.lightBlue.main,
                      border: `solid 1px ${colors?.lightBlue.main}`,
                    }}
                  >
                    {item.icon}
                  </IconButton>
                  <SuiTypography variant="body2" color="text" sx={{ my: 1 }}>
                    {item.label}
                  </SuiTypography>
                </SuiBox>
              ))}
            </SuiBox>
          </SuiBox>
        </SuiBox>
      )}
      <input
        type="file"
        id="imgupload"
        style={{ display: "none" }}
        ref={uploadRef}
        accept="image/*"
        onChange={handleOnFileChange}
        multiple
      />
      {importStyleVisible ? (
        <ImportStylePicturesModal
          handleClose={() => setImportStyleVisible(false)}
          visible={importStyleVisible}
          handleOk={handleAddStyle}
        />
      ) : null}
      {importMoodboardVisible ? (
        <ImportMoodboardPicture
          handleClose={() => setImportMoodboardVisible(false)}
          visible={importMoodboardVisible}
          handleOk={handleAddMoodboard}
          selectedMoodboards={[]}
          isMulti={true}
        />
      ) : null}
    </SuiBox>
  );
};

export default PhotoGrid;
